import {
  CellStyle,
  CellClassParams,
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  ITooltipParams,
  ValueFormatterParams,
} from "ag-grid-enterprise";
import { EditAndDeleteButtons } from "../../../../../../../shared/data-entries/EditAndDeleteButtons";
import { ManualAutoButton } from "../../../../../../../shared/data-entries/ManualAutoButton";
import { decimalMinutesToMmSs } from "@/utils/durationHelpers";

export type PlugType = "Inside Plug" | "Outside Plug";

type UsePlugMetricsTableDataProps = {
  onEdit: (plugType: PlugType) => void;
};

export type PlugMetricRow = {
  type: "Inside Plug" | "Outside Plug";
  Vol: number | null;
  Time: number | null;
  TotalTime: number | null;
  Heats: number | null;
  isManual?: boolean;
  warningHeats?: number | null;
  alertHeats?: number | null;
  warningMinutes?: number | null;
  alertMinutes?: number | null;
};

export const hasTimeWithoutVolume = (row?: PlugMetricRow) =>
  (row?.Time ?? 0) > 0 && !(row?.Vol ?? 0);

type PlugLifeLevel = "warning" | "alert";

const PLUG_LIFE_CELL_STYLE: Record<PlugLifeLevel, CellStyle> = {
  warning: { backgroundColor: "#FFE0B2" },
  alert: { backgroundColor: "#FFCDD2" },
};

type PlugLifeLimits = {
  warning: number | null | undefined;
  alert: number | null | undefined;
};

export const getPlugLifeLevel = (
  value: number | null | undefined,
  limits: PlugLifeLimits,
): PlugLifeLevel | undefined => {
  if (value == null) return undefined;
  if (limits.alert != null && value >= limits.alert) return "alert";
  if (limits.warning != null && value >= limits.warning) return "warning";
  return undefined;
};

const plugLifeTooltip = (
  level: PlugLifeLevel,
  metric: string,
  limits: PlugLifeLimits,
) => `${metric} on this plug reached the ${level} limit (${limits[level]}).`;

const plugLifeColumn = (
  metric: string,
  getLimits: (row?: PlugMetricRow) => PlugLifeLimits,
): Pick<ColDef<PlugMetricRow>, "cellStyle" | "tooltipValueGetter"> => ({
  cellStyle: (params: CellClassParams<PlugMetricRow>) => {
    const level = getPlugLifeLevel(params.value, getLimits(params.data));
    return level ? PLUG_LIFE_CELL_STYLE[level] : null;
  },
  tooltipValueGetter: (params: ITooltipParams<PlugMetricRow>) => {
    const limits = getLimits(params.data);
    const level = getPlugLifeLevel(params.value, limits);
    return level ? plugLifeTooltip(level, metric, limits) : null;
  },
});

const heatsLimits = (row?: PlugMetricRow): PlugLifeLimits => ({
  warning: row?.warningHeats,
  alert: row?.alertHeats,
});

const minutesLimits = (row?: PlugMetricRow): PlugLifeLimits => ({
  warning: row?.warningMinutes,
  alert: row?.alertMinutes,
});

const TIME_WITHOUT_VOLUME_TOOLTIP =
  "Stirring time logged with zero argon volume. Verify the flow meter and re-read from PLC.";

export const usePlugMetricsTableData = ({
  onEdit,
}: UsePlugMetricsTableDataProps) => {
  const columns: (ColDef | ColGroupDef)[] = [
    {
      headerName: "Plug Metrics",
      children: [
        {
          field: "manual-auto",
          headerName: " ",
          flex: 0.5,
          sortable: false,
          filter: false,
          cellRenderer: (params: ICellRendererParams<PlugMetricRow>) => {
            return (
              <ManualAutoButton isManual={params.data?.isManual ?? false} />
            );
          },
        },
        {
          headerName: "",
          field: "type",
          flex: 1,
          cellStyle: { fontWeight: "500" },
        },
        {
          field: "Vol",
          headerName: "Argon Vol [SCF]",
          flex: 1,
          valueFormatter: (params: ValueFormatterParams) => {
            return params.value != null ? params.value.toFixed(2) : "0.00";
          },
          tooltipValueGetter: (params: ITooltipParams<PlugMetricRow>) =>
            hasTimeWithoutVolume(params.data)
              ? TIME_WITHOUT_VOLUME_TOOLTIP
              : null,
        },
        {
          headerName: "Time [mm:ss]",
          field: "Time",
          flex: 1,
          valueFormatter: (params: ValueFormatterParams) => {
            return params.value != null
              ? decimalMinutesToMmSs(params.value)
              : "00:00";
          },
        },
        {
          headerName: "Total Time [min]",
          field: "TotalTime",
          flex: 1,
          ...plugLifeColumn("Minutes", minutesLimits),
          valueFormatter: (params: ValueFormatterParams) => {
            return params.value != null ? params.value.toFixed(2) : "0.00";
          },
        },
        {
          field: "Heats",
          headerName: "Heats",
          flex: 1,
          ...plugLifeColumn("Heats", heatsLimits),
        },
        {
          headerName: "",
          field: "actions",
          cellRenderer: EditAndDeleteButtons,
          cellRendererParams: (params: ICellRendererParams<PlugMetricRow>) => ({
            deleteItem: undefined as any,
            onEdit: () => onEdit((params.data as PlugMetricRow).type),
            id: 0,
          }),
          flex: 0.5,
          minWidth: 80,
          sortable: false,
          filter: false,
        },
      ],
    },
  ];

  return { columns };
};
